import type { InteractionConfig } from "../core/types"

/**
 * Standard Tailwind scale values (as percentages).
 * Non-standard values must use arbitrary syntax: scale-[1.03]
 */
const STANDARD_SCALE = new Set([0, 50, 75, 90, 95, 98, 100, 102, 105, 110, 125, 150])

/**
 * Standard Tailwind opacity values (as percentages).
 * v4 supports 0-100 in steps of 5.
 */
const STANDARD_OPACITY = new Set([0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100])

/** Generate a scale class with correct syntax */
function scaleClass(prefix: string, value: number): string {
  const pct = Math.round(value * 100)
  if (STANDARD_SCALE.has(pct)) return `${prefix}scale-${pct}`
  return `${prefix}scale-[${value}]`
}

/** Generate an opacity class with correct syntax */
function opacityClass(prefix: string, value: number): string {
  const pct = Math.round(value * 100)
  if (STANDARD_OPACITY.has(pct)) return `${prefix}opacity-${pct}`
  return `${prefix}opacity-[${value}]`
}

/** Generate translate classes with correct syntax */
function translateClasses(prefix: string, axis: "x" | "y", value: number): string {
  const prop = axis === "x" ? "translate-x" : "translate-y"
  if (value < 0) return `${prefix}-${prop}-${Math.abs(value)}`
  return `${prefix}${prop}-${value}`
}

/**
 * Resolves an InteractionConfig to Tailwind CSS utility classes.
 * Matches Flutter's InteractiveElement behavior using CSS pseudo-classes.
 */
export function resolveInteractionClasses(interaction: InteractionConfig | undefined): string {
  if (!interaction) return ""
  const classes: string[] = []

  // Group container
  if (interaction.group) classes.push("group")

  // Cursor
  if (interaction.cursor) classes.push(`cursor-${interaction.cursor}`)

  // Hover state
  if (interaction.hover) {
    const h = interaction.hover
    if (h.bg) classes.push(`hover:bg-${h.bg}`)
    if (h.color) classes.push(`hover:text-${h.color}`)
    if (h.scale != null) classes.push(scaleClass("hover:", h.scale))
    if (h.opacity != null) classes.push(opacityClass("hover:", h.opacity))
    if (h.shadow) classes.push(`hover:shadow-${h.shadow}`)
    if (h.ring != null) classes.push(`hover:ring-${h.ring}`)
    if (h.ringColor) classes.push(`hover:ring-${h.ringColor}`)
    if (h.translateX != null) classes.push(translateClasses("hover:", "x", h.translateX))
    if (h.translateY != null) classes.push(translateClasses("hover:", "y", h.translateY))
    if (h.rotate != null) classes.push(`hover:rotate-${h.rotate}`)
  }

  // Press (active) state
  if (interaction.press) {
    const p = interaction.press
    if (p.bg) classes.push(`active:bg-${p.bg}`)
    if (p.color) classes.push(`active:text-${p.color}`)
    if (p.scale != null) classes.push(scaleClass("active:", p.scale))
    if (p.opacity != null) classes.push(opacityClass("active:", p.opacity))
    if (p.shadow) classes.push(`active:shadow-${p.shadow}`)
  }

  // Focus state
  if (interaction.focus) {
    const f = interaction.focus
    if (f.bg) classes.push(`focus:bg-${f.bg}`)
    if (f.color) classes.push(`focus:text-${f.color}`)
    if (f.ring != null) classes.push(`focus:ring-${f.ring}`)
    if (f.ringColor) classes.push(`focus:ring-${f.ringColor}`)
    if (f.opacity != null) classes.push(opacityClass("focus:", f.opacity))
  }

  // Group-hover state
  if (interaction.groupHover) {
    const gh = interaction.groupHover
    if (gh.bg) classes.push(`group-hover:bg-${gh.bg}`)
    if (gh.color) classes.push(`group-hover:text-${gh.color}`)
    if (gh.scale != null) classes.push(scaleClass("group-hover:", gh.scale))
    if (gh.opacity != null) classes.push(opacityClass("group-hover:", gh.opacity))
    if (gh.shadow) classes.push(`group-hover:shadow-${gh.shadow}`)
    if (gh.translateX != null) classes.push(translateClasses("group-hover:", "x", gh.translateX))
    if (gh.translateY != null) classes.push(translateClasses("group-hover:", "y", gh.translateY))
  }

  // Transition
  if (interaction.transition) {
    const t = interaction.transition
    if (t.properties && t.properties.length > 0) {
      const props = t.properties.join(",")
      classes.push(`transition-[${props}]`)
    } else {
      classes.push("transition-all")
    }
    if (t.duration != null) classes.push(`duration-${t.duration}`)
    if (t.curve) {
      const curveMap: Record<string, string> = {
        easeInOut: "in-out",
        easeIn: "in",
        easeOut: "out",
        linear: "linear",
      }
      classes.push(`ease-${curveMap[t.curve] || t.curve}`)
    }
    if (t.delay != null) classes.push(`delay-${t.delay}`)
  }

  return classes.join(" ")
}
